import type { Isoline, ScalarGrid } from '../types';

type Segment = [ax: number, ay: number, bx: number, by: number];

/**
 * Which cell edges the contour crosses, per corner-classification case.
 *
 * Corners are indexed anticlockwise from the low corner - 0 low-left, 1 low-right, 2
 * high-right, 3 high-left - and a bit is set when that corner is at or above the
 * threshold. Edge i joins corner i to corner i+1.
 *
 * Cases 5 and 10 are the saddles and are absent here: the corners alone do not say which
 * way the contour turns, so they are resolved against the cell's centre in
 * {@link getSegments}.
 */
const CASE_EDGES: Record<number, [number, number][]> = {
  0: [],
  1: [[3, 0]],
  2: [[0, 1]],
  3: [[3, 1]],
  4: [[1, 2]],
  6: [[0, 2]],
  7: [[3, 2]],
  8: [[2, 3]],
  9: [[2, 0]],
  11: [[2, 1]],
  12: [[1, 3]],
  13: [[1, 0]],
  14: [[0, 3]],
  15: [],
};

/** The two corners each edge runs between, in the order the interpolation uses. */
const EDGE_CORNERS: [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 0],
];

/** Corner offsets from the cell's low corner, as (column, row). */
const CORNER_OFFSETS: [number, number][] = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

/**
 * Grid-coordinate resolution that crossings are snapped to, in steps per cell.
 *
 * Two cells sharing an edge compute the crossing on it from opposite ends, by expressions
 * that are algebraically equal and differ in their last bits. Stitching has to recognise
 * those as one point, so coordinates are snapped and the key is derived from the snapped
 * value - hashing at a coarser precision than the geometry is stored at is what lets two
 * distinct points share a key and drop out of their own polyline. A millionth of a cell is
 * far below any pixel this will ever be drawn at.
 */
const COORDINATE_STEPS_PER_CELL = 1e6;

const snap = (value: number): number =>
  Math.round(value * COORDINATE_STEPS_PER_CELL) / COORDINATE_STEPS_PER_CELL;

const interpolateEdge = (
  edge: number,
  column: number,
  row: number,
  corners: number[],
  threshold: number
): [number, number] => {
  const [fromCorner, toCorner] = EDGE_CORNERS[edge];
  const fromValue = corners[fromCorner];
  const toValue = corners[toCorner];
  const span = toValue - fromValue;
  /* A zero span means both ends sit exactly on the threshold, so any point along the edge
     is equally correct; the midpoint keeps the polyline continuous. */
  const t = span === 0 ? 0.5 : (threshold - fromValue) / span;

  const [fromX, fromY] = CORNER_OFFSETS[fromCorner];
  const [toX, toY] = CORNER_OFFSETS[toCorner];

  return [
    snap(column + fromX + (toX - fromX) * t),
    snap(row + fromY + (toY - fromY) * t),
  ];
};

/**
 * Resolves a saddle by asking whether the cell's middle is inside the contour.
 *
 * Two opposite corners above the threshold can mean either two separate peaks or one ridge
 * running through the cell, and the four corner values cannot distinguish them. Averaging
 * the corners approximates the bilinear surface at the centre, which is the standard
 * disambiguation - and picking arbitrarily instead is what produces contours that cross
 * themselves.
 */
const resolveSaddle = (
  caseIndex: number,
  corners: number[],
  threshold: number
): [number, number][] => {
  const centre = (corners[0] + corners[1] + corners[2] + corners[3]) / 4;
  const centreIsInside = centre >= threshold;
  /* Case 5 has corners 0 and 2 inside; case 10 has 1 and 3. When the centre joins the
     inside corners into one region, the contour instead wraps each outside corner. */
  const wrapsOddCorners = caseIndex === 5 ? centreIsInside : !centreIsInside;

  return wrapsOddCorners
    ? [
        [0, 1],
        [2, 3],
      ]
    : [
        [3, 0],
        [1, 2],
      ];
};

const getSegments = (grid: ScalarGrid, threshold: number): Segment[] => {
  const segments: Segment[] = [];

  for (let row = 0; row < grid.height - 1; row++) {
    for (let column = 0; column < grid.width - 1; column++) {
      const corners = CORNER_OFFSETS.map(
        ([dx, dy]) => grid.values[(row + dy) * grid.width + (column + dx)]
      );

      /* A cell touching a no-data corner is skipped whole. Interpolating across the
         boundary would invent a crossing position out of a value that does not exist, so
         the contour stops a cell short of the hull instead of guessing at it. */
      if (corners.some(value => value === null)) continue;
      const values = corners as number[];

      const caseIndex = values.reduce(
        (bits, value, index) =>
          value >= threshold ? bits | (1 << index) : bits,
        0
      );

      const edgePairs =
        caseIndex === 5 || caseIndex === 10
          ? resolveSaddle(caseIndex, values, threshold)
          : CASE_EDGES[caseIndex];

      for (const [fromEdge, toEdge] of edgePairs) {
        const [ax, ay] = interpolateEdge(
          fromEdge,
          column,
          row,
          values,
          threshold
        );
        const [bx, by] = interpolateEdge(
          toEdge,
          column,
          row,
          values,
          threshold
        );
        /* A segment with both ends at one point contributes no length and would seed a
           junk polyline during stitching. */
        if (ax === bx && ay === by) continue;
        segments.push([ax, ay, bx, by]);
      }
    }
  }

  return segments;
};

/** Safe as a plain join because every coordinate reaching here is already snapped. */
const pointKey = (x: number, y: number): string => `${x}|${y}`;

/**
 * Joins loose segments into polylines.
 *
 * Marching squares emits unordered fragments, which stroke fine but cannot carry a dash
 * pattern, a tapered width, or a label placed along the path - and at a fine resolution the
 * fragment count alone is worth collapsing. Walks each chain to its end, then back from the
 * start, so open contours that run off the grid edge come out whole rather than in halves.
 */
const stitch = (segments: Segment[], threshold: number): Isoline[] => {
  const endpoints = new Map<string, number[]>();
  segments.forEach(([ax, ay, bx, by], index) => {
    for (const key of [pointKey(ax, ay), pointKey(bx, by)]) {
      const existing = endpoints.get(key);
      if (existing) {
        existing.push(index);
        continue;
      }
      endpoints.set(key, [index]);
    }
  });

  const used = new Set<number>();

  const extend = (startX: number, startY: number): [number, number][] => {
    const path: [number, number][] = [];
    let [x, y] = [startX, startY];

    while (true) {
      const candidates = endpoints.get(pointKey(x, y)) ?? [];
      const nextIndex = candidates.find(index => !used.has(index));
      if (nextIndex === undefined) return path;

      used.add(nextIndex);
      const [ax, ay, bx, by] = segments[nextIndex];
      /* Whichever end matches where we are, step to the other one. */
      const matchesA = pointKey(ax, ay) === pointKey(x, y);
      const [nextX, nextY] = matchesA ? [bx, by] : [ax, ay];
      path.push([nextX, nextY]);
      x = nextX;
      y = nextY;
    }
  };

  const isolines: Isoline[] = [];

  segments.forEach(([ax, ay, bx, by], index) => {
    if (used.has(index)) return;
    used.add(index);

    const forward = extend(bx, by);
    const backward = extend(ax, ay);

    isolines.push({
      threshold,
      points: [...backward.reverse(), [ax, ay], [bx, by], ...forward],
    });
  });

  return isolines;
};

/**
 * Extracts isolines at each threshold, in grid coordinates.
 *
 * Grid coordinates rather than pixels on purpose: the same isolines are drawn flat by the
 * contour view and lifted onto the mesh by the 3D one, so the transform belongs to the
 * renderer rather than to the extraction.
 */
/**
 * Displaces a threshold off any value a sample might hold exactly.
 *
 * When a threshold equals a sample exactly, the crossing lands on a grid vertex that up to
 * four cells share, so four segments meet at one point and the walk cannot tell which arm
 * continues - a single ring comes back as a handful of fragments. Integer fields make this
 * the common case rather than a curiosity: a count field contoured at whole numbers hits it
 * at every level.
 *
 * Scaled to the field's own range so the displacement stays far below anything a reader
 * could see, while moving every crossing strictly inside an edge, where exactly two cells
 * meet it. The requested threshold is still what gets reported, so legends and labels are
 * unaffected.
 */
const nudge = (
  [min, max]: readonly [number, number],
  threshold: number
): number => {
  const span = max - min;
  return threshold + (span === 0 ? Number.EPSILON : span * 1e-10);
};

export const toIsolines = (
  grid: ScalarGrid,
  thresholds: number[]
): Isoline[] => {
  if (grid.width < 2 || grid.height < 2) return [];

  return thresholds.flatMap(threshold =>
    stitch(getSegments(grid, nudge(grid.zRange, threshold)), threshold)
  );
};

/**
 * Evenly spaced thresholds strictly inside the field's range.
 *
 * The extremes are excluded because a contour at exactly the minimum or maximum either
 * traces the whole boundary or collapses to a point, and neither tells the reader anything.
 */
export const toThresholds = (
  [min, max]: readonly [number, number],
  count: number
): number[] =>
  Array.from(
    { length: count },
    (_, index) => min + ((index + 1) / (count + 1)) * (max - min)
  );
